/*
PurchaseCreatePage - Componente para crear nuevas compras
Autor: Benjamín León
Fecha: 2024-12-07
*/
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import useVehicleFetcher from '../hooks/useVehicleFetcher'
import useClientFetcher from '../hooks/useClientFetcher'

import PurchaseEdit from './PurchaseEdit'
import { Alert } from './MessageInfo'

import Vehicle from '@/interfaces/Vehicle'
import Client from '@/interfaces/Client'
import { AppPath } from '@/enums/AppPath'

const PurchaseCreatePage: React.FC = () => {
  const { vehicle_id, client_id } = useParams<{ vehicle_id: string; client_id: string }>()
  const navigate = useNavigate()
  const { fetchVehicle, errorVehicle } = useVehicleFetcher()
  const { fetchClient, errorClient } = useClientFetcher()

  const [vehicle, setVehicle] = useState<Vehicle | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const fetchData = async () => {
      if (!vehicle_id || !client_id) {
        setError('ID de vehículo o cliente no proporcionado')

        return
      }

      try {
        // Fetch vehicle
        const vehicleData = await fetchVehicle(vehicle_id)

        if (!vehicleData) {
          setError('No se pudo cargar el vehículo')

          return
        }
        setVehicle(vehicleData)

        // Fetch client
        const clientData = await fetchClient(client_id)

        if (!clientData) {
          setError('No se pudo cargar el cliente')

          return
        }
        setClient(clientData)
      } catch (error) {
        console.error('Error fetching data:', error)
        setError('Error al cargar los datos')
      }
    }

    fetchData()
  }, [vehicle_id, client_id])

  const handleCreate = (id: string) => {
    // Navigate to purchase detail after successful creation
    navigate(`/${AppPath.Purchases}/view/${id}`)
  }

  // Show errors if any
  if (error || errorVehicle || errorClient) {
    return (
      <div className="w-full px-4">
        <Alert
          message={error || errorVehicle?.message || errorClient?.message || 'Error desconocido'}
          trigger={Date.now()}
          onTimeout={() => {}}
        />
        <button
          className="mt-4 bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          onClick={() => navigate(-1)}
        >
          Volver
        </button>
      </div>
    )
  }

  // Show loading state
  if (!vehicle || !client) {
    return (
      <div className="w-full px-4">
        <p>Cargando datos...</p>
      </div>
    )
  }

  return (
    <div className="w-full px-4">
      <PurchaseEdit vehicle={vehicle} client={client} onCreate={handleCreate} />
    </div>
  )
}

export default PurchaseCreatePage
